import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Refresh, RestartAlt, Search, Visibility } from "@mui/icons-material";
import PageHeaderLayout from "../../../components/Layout/PageHeaderLayout";
import { commonRequest } from "../../../services/ApiCall";
import { Helpers } from "../../../services/Helpers";
import Loading from "../../../components/Loader/Loading";
import CustomPagination from "../../../components/pagination/CustomPagination";

const defaults = {
  requestId: "",
  method: "",
  endpoint: "",
  userId: "",
  statusCode: "",
  success: "",
  startDate: "",
  endDate: "",
};

const formatDate = (value) => value ? new Intl.DateTimeFormat("en-IN", {
  dateStyle: "medium",
  timeStyle: "medium",
}).format(new Date(value)) : "—";

const JsonBlock = ({ title, value }) => value == null ? null : (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
    <Box component="pre" sx={{
      bgcolor: "grey.100",
      borderRadius: 1,
      fontSize: "0.75rem",
      m: 0,
      maxHeight: 280,
      overflow: "auto",
      p: 1.5,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}>
      {JSON.stringify(value, null, 2)}
    </Box>
  </Box>
);

export default function ApiLogs() {
  const dispatch = useDispatch();
  const { control, handleSubmit, reset, setError, clearErrors } = useForm({ defaultValues: defaults });
  const [filters, setFilters] = useState(defaults);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setPageError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "api-logs" });
  }, [dispatch]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setPageError("");
    const params = new URLSearchParams({
      page: String(paginationModel.page + 1),
      limit: String(paginationModel.pageSize),
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "") {
        params.set(key, key.endsWith("Date") ? new Date(value).toISOString() : value);
      }
    });
    const response = await commonRequest(
      "GET",
      `${Helpers.URL()}/api/admin/api-audit-logs?${params}`,
      {},
      Helpers.token()
    );
    if (response?.success === 1) {
      setRows(response.data.logs || []);
      setRowCount(response.data.totalItems || 0);
    } else {
      setRows([]);
      setRowCount(0);
      setPageError(response?.message || "Unable to load API logs");
    }
    setLoading(false);
  }, [filters, paginationModel]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const applyFilters = (values) => {
    clearErrors();
    if (values.startDate && values.endDate && new Date(values.startDate) > new Date(values.endDate)) {
      setError("endDate", { message: "End date must be after start date" });
      return;
    }
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFilters(values);
  };

  const clearFilters = () => {
    reset(defaults);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFilters(defaults);
  };

  const columns = useMemo(() => [
    { field: "timestamp", headerName: "Date & time", width: 185, valueFormatter: ({ value }) => formatDate(value) },
    {
      field: "method", headerName: "Method", width: 90,
      renderCell: ({ value }) => <Chip label={value} size="small" color={value === "GET" ? "info" : "primary"} />,
    },
    {
      field: "endpoint", headerName: "Endpoint", minWidth: 260, flex: 1,
      renderCell: ({ value }) => <Tooltip title={value || ""}><Typography variant="body2" noWrap>{value}</Typography></Tooltip>,
    },
    { field: "action", headerName: "Action", width: 170 },
    { field: "module", headerName: "Module", width: 130 },
    {
      field: "userId", headerName: "User", width: 150,
      valueGetter: ({ row }) => row.userName || row.userEmail || row.userId || "Anonymous",
    },
    {
      field: "statusCode", headerName: "Status", width: 90,
      renderCell: ({ row }) => <Chip label={row.statusCode} size="small" color={row.success ? "success" : "error"} />,
    },
    {
      field: "responseTimeMs", headerName: "Time", width: 105,
      valueFormatter: ({ value }) => `${Number(value || 0).toFixed(2)} ms`,
    },
    {
      field: "details", headerName: "Details", width: 90, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View complete audit log">
          <Button size="small" aria-label="View complete audit log" onClick={() => setSelected(row)}>
            <Visibility fontSize="small" />
          </Button>
        </Tooltip>
      ),
    },
  ], []);

  return (
    <>
      <PageHeaderLayout headerTitle={`API Logs (${rowCount})`}>
        <Button size="small" startIcon={<Refresh />} onClick={loadLogs}>Refresh</Button>
      </PageHeaderLayout>

      <Paper component="form" onSubmit={handleSubmit(applyFilters)} sx={{ mb: 1, p: { xs: 1, md: 1.5 } }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name="requestId" control={control} render={({ field }) => (
              <TextField {...field} label="Request ID" size="small" fullWidth />
            )} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name="endpoint" control={control} render={({ field }) => (
              <TextField {...field} label="Endpoint" size="small" fullWidth />
            )} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Controller name="method" control={control} render={({ field }) => (
              <FormControl size="small" fullWidth>
                <InputLabel>Method</InputLabel>
                <Select {...field} label="Method">
                  <MenuItem value="">All</MenuItem>
                  {["GET", "POST", "PUT", "PATCH", "DELETE"].map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Controller name="success" control={control} render={({ field }) => (
              <FormControl size="small" fullWidth>
                <InputLabel>Result</InputLabel>
                <Select {...field} label="Result">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Success</MenuItem>
                  <MenuItem value="false">Failed</MenuItem>
                </Select>
              </FormControl>
            )} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Controller name="statusCode" control={control} render={({ field }) => (
              <TextField {...field} label="Status code" type="number" size="small" fullWidth />
            )} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Controller name="userId" control={control} render={({ field }) => (
              <TextField {...field} label="User ID" size="small" fullWidth />
            )} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name="startDate" control={control} render={({ field }) => (
              <TextField {...field} label="From" type="datetime-local" size="small" fullWidth InputLabelProps={{ shrink: true }} />
            )} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller name="endDate" control={control} render={({ field, fieldState }) => (
              <TextField {...field} label="To" type="datetime-local" size="small" fullWidth InputLabelProps={{ shrink: true }} error={!!fieldState.error} helperText={fieldState.error?.message} />
            )} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
              <Button type="button" variant="outlined" startIcon={<RestartAlt />} onClick={clearFilters}>Clear</Button>
              <Button type="submit" variant="contained" startIcon={<Search />}>Apply filters</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Card>
        <Box sx={{ overflow: "auto" }}>
          <Box sx={{
            width: "100%",
            display: "table",
            tableLayout: "fixed",
            bgcolor: "#fff",
            borderRadius: ".5rem",
            "& .odd": { bgcolor: "white" },
            "& .even": { bgcolor: "#ECECEC" },
          }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row._id}
              loading={loading}
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              showFirstButton
              showLastButton
              disableColumnMenu
              disableDensitySelector
              disableRowSelectionOnClick
              autoHeight
              getRowHeight={() => "auto"}
              getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"}
              slots={{ loadingOverlay: Loading, pagination: CustomPagination }}
              sx={{
                minWidth: "100%",
                "& .MuiDataGrid-root": { overflowX: "auto" },
                "& .MuiDataGrid-footerContainer": { display: "block" },
                "& .MuiDataGrid-row .MuiDataGrid-cell": { py: 1, fontSize: ".65rem" },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "2px solid rgba(224, 224, 224, 1)",
                  borderTop: "2px solid rgba(224, 224, 224, 1)",
                  fontWeight: 600,
                  fontSize: ".75rem",
                  p: 0,
                  minHeight: "0 !important",
                  height: "2rem",
                },
                "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#3e3e3e" },
              }}
            />
          </Box>
        </Box>
      </Card>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="lg" fullScreen={false}>
        <DialogTitle>API log details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Grid container spacing={1}>
                {[
                  ["Request ID", selected.requestId],
                  ["Called at", formatDate(selected.timestamp)],
                  ["Method", selected.method],
                  ["Status", `${selected.statusCode} (${selected.success ? "Success" : "Failed"})`],
                  ["Endpoint", selected.endpoint],
                  ["Original URL", selected.originalUrl],
                  ["Action", selected.action],
                  ["Module", selected.module],
                  ["User", selected.userName || selected.userEmail || selected.userId || "Anonymous"],
                  ["Role", selected.userRole],
                  ["IP address", selected.ipAddress],
                  ["Response time", `${Number(selected.responseTimeMs || 0).toFixed(3)} ms`],
                  ["Resource", [selected.resourceType, selected.resourceId].filter(Boolean).join(" #")],
                  ["Expires at", formatDate(selected.expiresAt)],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{value || "—"}</Typography>
                  </Grid>
                ))}
              </Grid>
              <JsonBlock title="Query parameters" value={selected.query} />
              <JsonBlock title="Route parameters" value={selected.params} />
              <JsonBlock title="Safe request body" value={selected.requestBody} />
              <JsonBlock title="Before and after changes" value={selected.changes} />
              <JsonBlock title="Error" value={selected.error} />
              <JsonBlock title="Metadata" value={selected.metadata} />
              <JsonBlock title="Complete stored event" value={selected} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
}
